/**
 * Radacina aplicatiei.
 *
 * Ordinea e obligatorie: identitatea pietei -> bootstrap -> tema -> rute.
 * Nimic nu se randeaza inainte de config, fiindca fiecare culoare, text si
 * politica de pe ecran vin de acolo (ADR-011). O vitrina care porneste cu
 * valorile ei si le inlocuieste dupa o secunda arata ca o alta platforma.
 */

import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { ApiError, fetchBootstrap, resolveMarketKey } from "./api/client";
import { AddressSheetProvider } from "./components/AddressSheetProvider";
import { BootstrapProvider } from "./components/BootstrapProvider";
import { CartProvider } from "./components/CartProvider";
import { CoverageProvider } from "./components/CoverageProvider";
import { ConsentProvider } from "./consent/ConsentProvider";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Layout } from "./components/Layout";
import { EmptyState, ErrorState, LoadingState } from "./components/StateScreen";
import { ToastProvider } from "./components/ToastProvider";
import { useLoadable } from "./components/useLoadable";
import { Cart } from "./pages/Cart";
import { Checkout } from "./pages/Checkout";
import { Home } from "./pages/Home";
import { Menu } from "./pages/Menu";
import { Legal } from "./pages/Legal";
import { Orders } from "./pages/Orders";
import { OrderTracking } from "./pages/OrderTracking";
import { Page } from "./pages/Page";
import { Vendor } from "./pages/Vendor";
import { applyTheme } from "./theme/from-config";

interface MarketIdentity {
  key: string | null;
  error: ApiError | null;
}

function useMarketIdentity(): MarketIdentity {
  const [identity] = useState<MarketIdentity>(() => {
    try {
      return { key: resolveMarketKey(), error: null };
    } catch (error: unknown) {
      return {
        key: null,
        error: new ApiError({
          status: 0,
          code: "MARKET_KEY_UNRESOLVED",
          message: error instanceof Error ? error.message : "Piața nu a putut fi identificată.",
        }),
      };
    }
  });
  return identity;
}

export function App() {
  const { key, error: identityError } = useMarketIdentity();

  const bootstrap = useLoadable(
    (signal) =>
      key === null
        ? Promise.reject(identityError ?? new Error("Piața nu a putut fi identificată."))
        : fetchBootstrap(key, signal),
    [key],
  );

  const config = bootstrap.data?.config ?? null;

  useEffect(() => {
    if (config === null) return;
    return applyTheme(config, document.documentElement);
  }, [config]);

  useEffect(() => {
    if (bootstrap.data === null || config === null) return;
    // `lang` se scrie in `BootstrapProvider`, care stie limba ACTIVA.

    // Route-specific metadata belongs to Layout, which also handles direct
    // entry and the home page. A bootstrap effect here would overwrite it.

    // `robots` SE SI STERGE la golire — un `noindex` ramas lipit dupa ce cineva
    // l-a scos din config ar tine vitrina in afara Google pe termen nedefinit.
    const robots = config.seo?.robots;
    const etichetaRobots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (typeof robots === "string" && robots.trim().length > 0) {
      if (etichetaRobots === null) {
        const tag = document.createElement("meta");
        tag.name = "robots";
        tag.content = robots.trim();
        document.head.appendChild(tag);
      } else {
        etichetaRobots.content = robots.trim();
      }
    } else if (etichetaRobots !== null) {
      etichetaRobots.remove();
    }

    const favicon = config.branding.faviconUrl;
    if (typeof favicon === "string" && favicon.length > 0) {
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (link === null) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = favicon;
    }

    // Culoarea barei browserului pe telefon urmeaza brandul pietei: fara ea,
    // antetul aplicatiei e in culoarea pietei, iar bara de deasupra lui in alb.
    const themeColor = config.theme.primaryColor;
    if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(themeColor)) {
      let tag = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (tag === null) {
        tag = document.createElement("meta");
        tag.name = "theme-color";
        document.head.appendChild(tag);
      }
      tag.content = themeColor;
    }
  }, [bootstrap.data, config]);

  const content = useMemo(() => {
    if (bootstrap.state === "loading") return <LoadingState label="Se încarcă platforma" />;
    if (bootstrap.state === "error" || bootstrap.data === null || key === null) {
      const shown = bootstrap.error ?? identityError;
      return (
        <ErrorState
          title="Platforma nu a putut porni"
          error={
            shown ??
            new ApiError({
              status: 0,
              code: "UNEXPECTED",
              message: "Configurația pieței nu a putut fi încărcată.",
            })
          }
          onRetry={bootstrap.reload}
        />
      );
    }
    return null;
  }, [bootstrap.state, bootstrap.data, bootstrap.error, bootstrap.reload, identityError, key]);

  if (content !== null) {
    return (
      <div className="mx-auto flex min-h-screen max-w-app items-center justify-center px-4">{content}</div>
    );
  }

  if (bootstrap.data === null || key === null) return null;

  return (
    <ErrorBoundary>
      <BootstrapProvider marketKey={key} bootstrap={bootstrap.data}>
        {/* Consimtamantul sta INAINTE de rute, nu intr-o pagina: bannerul
            trebuie sa apara la prima vizita, oricare ar fi adresa deschisa, iar
            scripturile de urmarire nu au voie sa porneasca inaintea deciziei. */}
        <ConsentProvider>
          <CartProvider marketKey={key}>
            {/* Acoperirea depinde de adresa din sesiune, deci sta sub cos. */}
            <CoverageProvider>
              <ToastProvider>
                {/* O singura fereastra de adresa, peste rute: paginile o deschid,
                    nu o monteaza. */}
                <AddressSheetProvider>
                <BrowserRouter>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/meniu" element={<Menu />} />
                      <Route path="/vitrina/:slug" element={<Vendor />} />
                      <Route path="/cos" element={<Cart />} />
                      <Route path="/finalizare" element={<Checkout />} />
                      <Route path="/comenzi" element={<Orders />} />
                      <Route path="/comanda/:code" element={<OrderTracking />} />
                      <Route path="/legal" element={<Legal />} />
                      <Route path="/legal/:document" element={<Legal />} />
                      {/* Paginile compuse in builder. Prefixul `/p/` le tine intr-un
                          spatiu propriu: fara el, o pagina numita „cos" ar fi acoperit
                          coșul aplicatiei. */}
                      <Route path="/p/:slug" element={<Page />} />
                      <Route
                        path="*"
                        element={
                          <EmptyState
                            title="Pagina nu există"
                            description="Adresa deschisă nu corespunde niciunei pagini din aplicație."
                          />
                        }
                      />
                    </Routes>
                  </Layout>
                </BrowserRouter>
                </AddressSheetProvider>
              </ToastProvider>
            </CoverageProvider>
          </CartProvider>
        </ConsentProvider>
      </BootstrapProvider>
    </ErrorBoundary>
  );
}
